import { useState } from 'react'
import { Button } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { Video, VideoOff, X } from 'lucide-react'

// Livebild einer Drohne (DJI Cloud API Livestream): DJI Pilot 2 sendet per RTMP an MediaMTX,
// der Browser zeigt den HLS-Player von MediaMTX über das Frontend unter /live/<pfad>/.

export interface LivebildInfo {
  laeuft: boolean
  url: string | null
  anforderbar: boolean
  /** Livestream-Freigabe der Fernsteuerung: laeuft | bereit | unbekannt | nicht_freigegeben */
  freigabe?: { stufe: 'laeuft' | 'bereit' | 'unbekannt' | 'nicht_freigegeben'; text: string }
  dji_meldet_stream: boolean
  meldung: string | null
  /** Aktive Linse des Stroms: normal / wide / zoom / ir (legt der Pilot fest) */
  linse?: string | null
  /** Ströme, die die Fernsteuerung gleichzeitig übertragen kann (RC: 1) */
  gleichzeitig_max?: number | null
  kameras?: LivebildKamera[]
}

export interface LivebildKamera {
  video_id: string
  camera_index: string
  /** false = Kamera der Fernsteuerung selbst */
  drohne: boolean
  linse: string | null
  linsen: string[]
  aktiv: boolean
}

/** Knöpfe und Status in der Drohnen-Seitenleiste. steuerbar = Anfordern/Beenden per Cloud (Führung). */
export function LivebildLeiste({ sn, livebild, steuerbar, onAnsehen, onGeaendert }: {
  sn: string
  livebild: LivebildInfo | undefined
  steuerbar: boolean
  onAnsehen: () => void
  onGeaendert?: () => void
}) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  if (!livebild) return null

  async function steuern(aktion: 'start' | 'stop') {
    setLaeuft(true); setFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(sn)}/livebild/${aktion}`)
      onGeaendert?.()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Livebild konnte nicht gesteuert werden')
    } finally { setLaeuft(false) }
  }

  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1">
        {livebild.url ? (
          <Button size="sm" className="flex-1" onClick={onAnsehen}><Video className="mr-1 h-4 w-4" />Livebild</Button>
        ) : (
          <div className="flex flex-1 items-center gap-1 text-xs text-dark-text-subtle"><VideoOff className="h-3.5 w-3.5" />kein Livebild</div>
        )}
        {steuerbar && !livebild.laeuft && livebild.anforderbar && (
          <Button size="sm" variant="secondary" disabled={laeuft} onClick={() => steuern('start')}>Anfordern</Button>
        )}
        {steuerbar && (livebild.laeuft || livebild.dji_meldet_stream) && (
          <Button size="sm" variant="secondary" disabled={laeuft} onClick={() => steuern('stop')}>Beenden</Button>
        )}
      </div>
      {livebild.freigabe && <FreigabeAnzeige freigabe={livebild.freigabe} />}
      {(fehler || livebild.meldung) && (
        <div className={`text-xs ${fehler ? 'text-red-300' : 'text-dark-text-subtle'}`}>{fehler || livebild.meldung}</div>
      )}
    </div>
  )
}

/** Großes Fenster mit dem Livebild (Player von MediaMTX, lädt bei Unterbrechung selbst neu). */
export function LivebildFenster({ titel, url, onSchliessen }: { titel: string; url: string | null; onSchliessen: () => void }) {
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" onClick={onSchliessen}>
      <div className="w-full max-w-5xl overflow-hidden rounded-xl border border-dark-border bg-dark-bg-secondary" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-dark-border px-4 py-2">
          <span className="text-sm font-semibold text-dark-text">Livebild · {titel}</span>
          <button type="button" className="flex h-11 w-11 items-center justify-center rounded-lg text-dark-text-muted hover:text-dark-text" onClick={onSchliessen} aria-label="Schließen"><X className="h-5 w-5" /></button>
        </div>
        <div className="relative aspect-video w-full bg-black">
          {url ? (
            <iframe src={url} title={`Livebild ${titel}`} className="absolute inset-0 h-full w-full" allow="autoplay; fullscreen" allowFullScreen />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-dark-text-muted">Kein Bild – die Drohne sendet derzeit nicht.</div>
          )}
        </div>
      </div>
    </div>
  )
}


const FREIGABE_STIL: Record<string, string> = {
  laeuft: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
  bereit: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
  unbekannt: 'border-amber-500/60 bg-amber-500/10 text-amber-200',
  nicht_freigegeben: 'border-red-500/60 bg-red-500/10 text-red-200',
}

/** Zustand der Livestream-Freigabe (aus den Meldungen der Fernsteuerung, nicht aus einem erfolgreichen Aufruf). */
export function FreigabeAnzeige({ freigabe }: { freigabe: NonNullable<LivebildInfo['freigabe']> }) {
  return (
    <div className={`rounded border px-2 py-1 text-[11px] font-medium ${FREIGABE_STIL[freigabe.stufe] || FREIGABE_STIL.unbekannt}`} data-testid="livestream-freigabe" data-stufe={freigabe.stufe}>
      {freigabe.text}
    </div>
  )
}
